import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, Download, RefreshCw, AlertTriangle, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SecureBackupPage = () => {
  const { token } = useParams();
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const fetchInfo = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/secure-backup/${token}/info`);
      setInfo(resp.data);
    } catch (err) {
      setError(err.response?.data?.detail || "This backup link is not valid.");
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchInfo();
  }, [fetchInfo]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/secure-backup/${token}`, {
        responseType: "blob",
        timeout: 300000,
      });
      const cd = resp.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `backup_${Date.now()}.bin`;
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success("Backup downloaded — this link is now spent.");
      setInfo((prev) => (prev ? { ...prev, used: true } : prev));
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to download backup.";
      toast.error(detail);
      setError(detail);
    }
    setDownloading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <RefreshCw className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white px-4 py-10">
      <div className="max-w-xl mx-auto">
        <Card className="border-2 shadow-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-brand-primary">
              <Shield className="w-6 h-6" />
              Secure Backup Download
            </CardTitle>
            <CardDescription>
              A single-use link issued by your weekly backup email.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div data-testid="secure-backup-error">{error}</div>
              </div>
            )}

            {info && !error && (
              <div className="text-sm text-ink-muted space-y-1">
                <div>
                  Type:{" "}
                  <strong className="text-ink-main">
                    {info.kind === "full_backup_json"
                      ? "Full data backup (JSON)"
                      : "All documents (ZIP)"}
                  </strong>
                </div>
                <div>
                  Expires:{" "}
                  <strong className="text-ink-main">
                    {new Date(info.expiresAt).toLocaleString()}
                  </strong>
                </div>
                {info.used && (
                  <div className="flex items-center gap-1 text-amber-700 mt-2">
                    <AlertTriangle className="w-4 h-4" /> This link has already been used.
                  </div>
                )}
                {info.expired && !info.used && (
                  <div className="flex items-center gap-1 text-red-700 mt-2">
                    <AlertTriangle className="w-4 h-4" /> This link has expired.
                  </div>
                )}
              </div>
            )}

            {!user && info && !info.used && !info.expired && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                You must be signed in as the owner to redeem this link.{" "}
                <a
                  href={`/login?next=${encodeURIComponent(window.location.pathname)}`}
                  className="underline font-semibold"
                  data-testid="secure-backup-login-link"
                >
                  Sign in →
                </a>
              </div>
            )}

            {user && info && !info.used && !info.expired && !error && (
              <Button
                onClick={handleDownload}
                disabled={downloading}
                data-testid="secure-backup-download-btn"
                className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white rounded-xl"
              >
                {downloading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Download now
              </Button>
            )}

            {info?.used && (
              <div className="flex items-center gap-2 text-green-700 text-sm">
                <CheckCircle className="w-5 h-5" /> Download completed for this link.
              </div>
            )}

            <p className="text-xs text-ink-muted pt-2 border-t">
              For your security, every link is single-use and expires automatically. Need a
              fresh one? Wait for next Monday's email or contact{" "}
              <a href="mailto:admin@simplesignin.com" className="text-brand-primary">
                admin@simplesignin.com
              </a>
              .
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SecureBackupPage;
